import React, {useCallback, useContext, useMemo} from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import Styles from '../../Styles';
import {MoreWaysToEarnPolicyModel} from 'src/types/RewardsModel';
import AVText from 'src/components/elements/AVText';
import CashRewardsIcon from 'src/components/img/svg/rewards/CashRewardsIcon';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import {RewardPolicyType} from 'src/types/Rewards';
import {RewardsIcon} from 'src/components/img/svg/rewards';
import Localized from 'src/constants/AppStrings';
import AppRoutes from 'src/AppRoutes';
import NavActions from 'src/actions/NavActions';
import Util, {
  rewardsCancelPolicyInBackground,
  rewardsStartPolicyInBackground,
  submitAppSurveyInBackground,
} from 'src/Util';
import AccountStore from 'src/stores/AccountStore';
import withIsConnected from 'src/components/hoc/withIsConnected';
import {IsConnectedProps} from 'src/types/Screen';
import MainConsumerContext from 'src/components/MainConsumerContext';
import {RewardsModal} from 'src/components/elements/RewardsModal';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';
import RewardsCompleteButton from 'src/components/elements/rewards/RewardsCompleteButton';
import {getToastDescriber} from 'src/components/elements/toastDescriptor/DescroiptorType';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import Events from 'src/logging/Events';
import {RootState} from 'src/redux/store';
import {useSelector} from 'react-redux';
import AccountConstants from 'src/constants/AccountConstants';

type RewardMoreWaysToEarnViewProps = IsConnectedProps &
  WithGlobalizeProps & {
    policyItem: MoreWaysToEarnPolicyModel;
    index?: number;
    isAutoFundDisabled?: boolean | null;
  };
type ModalParams = {
  minimumSpendAmount?: number;
  returnWithinDays?: number;
  endDate?: string;
  requiredPurchaseCount?: number;
  currentPurchaseCount?: number;
  policyType?: string;
};

const totalHorizontalPadding = 32;
const gapBetweenColumns = 12;
const availableWidth =
  Styles.ScreenWidth - totalHorizontalPadding - gapBetweenColumns;
const totalWidth =
  Platform.OS === 'web' ? availableWidth * 0.55 : availableWidth;
const cardWidth = totalWidth / Util.getNumberOfColumnsInGrid();

const RewardMoreWaysToEarnView = (props: RewardMoreWaysToEarnViewProps) => {
  const creditCards = useSelector((s: RootState) => s.account.creditCards);
  const [isModalVisible, setModalVisible] = React.useState(false);
  const [modalParams, setModalParams] = React.useState<ModalParams>({});

  const context = useContext(MainConsumerContext);
  const updateDescriptionWithAmount = useCallback((amount: number) => {
    const currencyValue = Util.formatCurrency(
      props,
      amount ?? 0,
      props.policyItem?.currency ?? AccountStore.getCurrency(),
      {minimumFractionDigits: 0, maximumFractionDigits: 2},
    );
    return props.policyItem.policyDescription.replace('$X', currencyValue);
  }, []);

  const isAddCard =
    props.policyItem?.policy?.policyType === RewardPolicyType.ADD_CREDIT_CARD;

  const isInitiated =
    String(props.policyItem?.currentStatus).toUpperCase() ===
    AccountConstants.INITIATED;

  const underCap =
    props.policyItem?.redemptionTimes < props.policyItem?.policy?.redemptionCap;

  const hasNoCards = creditCards.length === 0;

  /** Enable retry when "Add Credit Card" policy is INITIATED and the account has no cards. */
  const enableAddCardRetry = isAddCard && isInitiated && underCap && hasNoCards;

  const currentStatusForBtn = useMemo(() => {
    if (
      props.policyItem?.policy?.policyType ===
        RewardPolicyType.ADD_PHONE_NUMBER &&
      props.policyItem?.currentStatus === AccountConstants.INITIATED
    ) {
      return AccountConstants.NOT_INITIATED;
    }
    return enableAddCardRetry ? undefined : props.policyItem?.currentStatus;
  }, [props.policyItem]);

  const getPolicyDescription = useCallback(() => {
    switch (props.policyItem.policy.policyType) {
      case RewardPolicyType.LOAD_WALLET_MIN_AMOUNT:
        return updateDescriptionWithAmount(
          props.policyItem?.policy?.minLoadAmount,
        );
      case RewardPolicyType.MIN_SPEND:
        return updateDescriptionWithAmount(
          props.policyItem?.policy?.minimumSpendAmount,
        );
      default:
        return props.policyItem.policyDescription;
    }
  }, []);

  const isCompleted = useMemo(() => {
    const base =
      props.policyItem?.redemptionTimes >=
      props.policyItem?.policy?.redemptionCap;

    /** "Add Credit Card" attempt failed; policy remains incomplete. */
    if (isAddCard && isInitiated && underCap && hasNoCards) {
      return false;
    }
    return base;
  }, [
    props.policyItem?.redemptionTimes,
    props.policyItem?.policy?.redemptionCap,
    isAddCard,
    isInitiated,
    underCap,
    hasNoCards,
  ]);

  const getRewardImage = useMemo(() => {
    switch (props.policyItem.policy.policyType) {
      case RewardPolicyType.LOAD_WALLET_MIN_AMOUNT:
      case RewardPolicyType.ADD_CREDIT_CARD:
      case RewardPolicyType.AUTO_RELOAD:
        return (
          <CashRewardsIcon color={getLayoutDescriber().bottomTabBtnColor()} />
        );
      default:
        return <RewardsIcon />;
    }
  }, []);

  const moreWaysToEarnModal = (modalParams = {}) => {
    setModalParams(modalParams);
    setModalVisible(true);
  };

  const handleCompleteAction = useCallback(() => {
    const {policyType} = props?.policyItem?.policy;
    // Navigate to Selected Reward More Ways To Earn Flow
    switch (policyType) {
      case RewardPolicyType.ADD_CREDIT_CARD:
        NavActions.navigate(AppRoutes.RewardsAddCreditCard, {
          rewardProgramId: props.policyItem?.rewardPolicyId,
        });
        break;
      case RewardPolicyType.ADD_BIRTHDAY:
        NavActions.navigate(AppRoutes.RewardsAddBirthday, {
          rewardProgramId: props.policyItem?.rewardPolicyId,
        });
        break;
      case RewardPolicyType.LOAD_WALLET_MIN_AMOUNT:
        context.actions.navigateToFunding(true, {
          transDate: Util.getCurrentDate(),
          rewardProgramId: props.policyItem?.rewardPolicyId,
          rewardsMinLoadAmount: props.policyItem?.policy?.minLoadAmount,
        });
        break;
      case RewardPolicyType.AUTO_RELOAD:
        NavActions.navigate(AppRoutes.AutoReloadPanel, {
          rewardProgramId: props.policyItem?.rewardPolicyId,
          rewardsAutoReloadAmount: props.policyItem?.policy?.autoReloadAmount,
          rewardsAutoReloadBy: props.policyItem?.policy?.autoReloadBy,
        });
        break;
      case RewardPolicyType.MIN_SPEND:
        moreWaysToEarnModal({
          minimumSpendAmount: props.policyItem?.policy?.minimumSpendAmount,
        });
        break;
      case RewardPolicyType.COME_BACK_SOON:
        moreWaysToEarnModal({
          returnWithinDays: props.policyItem?.policy?.returnWithinDays,
        });
        break;
      case RewardPolicyType.PURCHASE_STREAK:
        moreWaysToEarnModal({
          requiredPurchaseCount:
            props.policyItem?.policy?.requiredPurchaseCount,
          endDate: props.policyItem?.policy?.endDate,
          currentPurchaseCount: props.policyItem?.currentPurchaseCount,
          policyType: props.policyItem?.policy?.policyType,
        });
        break;
      case RewardPolicyType.APP_SURVEY:
        moreWaysToEarnModal({
          requiredPurchaseCount:
            props.policyItem?.policy?.requiredPurchaseCount,
          endDate: props.policyItem?.policy?.endDate,
          currentPurchaseCount: props.policyItem?.currentPurchaseCount,
          policyType: props.policyItem?.policy?.policyType,
        });
        break;
      case RewardPolicyType.ADD_PHONE_NUMBER:
        NavActions.navigate(AppRoutes.AddPhoneNumber, {
          rewardPolicyId: props.policyItem?.rewardPolicyId,
          fromRewards: true,
        });
        break;

      default:
        // Safe fallback (e.g., to the Rewards hub)
        NavActions.navigate(AppRoutes.RewardsScreen);
        break;
    }
  }, [props.policyItem]);

  const handleStartShopping = useCallback(
    async (answers?: {quest1: string; quest2?: string}) => {
      const accountId = AccountStore.getAccountId();
      const rewardProgramPolicyId = props.policyItem.rewardPolicyId;
      const isAppSurvey =
        props.policyItem?.policy?.policyType === RewardPolicyType.APP_SURVEY;

      try {
        context.actions.showSpinner();

        await rewardsStartPolicyInBackground(
          accountId,
          rewardProgramPolicyId,
          'Rewards More Ways To Earn',
        );

        if (isAppSurvey && answers?.quest1) {
          const {surveyQuestions = [], surveyTitle = ''} =
            props.policyItem.policy;

          const responses = [];

          if (surveyQuestions[0] && answers.quest1) {
            responses.push({
              question: surveyQuestions[0],
              answer: answers.quest1,
            });
          }

          if (surveyQuestions[1] && answers.quest2) {
            responses.push({
              question: surveyQuestions[1],
              answer: answers.quest2,
            });
          }

          try {
            await submitAppSurveyInBackground(
              accountId,
              rewardProgramPolicyId,
              responses,
              surveyTitle,
              'Rewards App Survey',
            );
          } catch (surveyError) {
            await rewardsCancelPolicyInBackground(
              accountId,
              rewardProgramPolicyId,
              'Rewards App Survey',
            );
            CrashlyticsEvents.log(
              'Exception',
              'Rewards App Survey',
              generateErrorMessage(surveyError),
            );
            Events.Error.trackEvent(
              'Exception',
              'Rewards App Survey',
              generateErrorMessage(surveyError),
            );
          }
        }
        if (isAppSurvey) {
          NavActions.navigate(AppRoutes.RewardsScreen, {
            refreshRewards: true,
            suppressRedirect: true,
          });
        } else {
          if (Platform.OS === 'web') {
            NavActions.reset(AppRoutes.Home);
          } else {
            NavActions.reset(AppRoutes.HomeTab);
          }
        }

        setModalVisible(false);
      } catch (error) {
        CrashlyticsEvents.log(
          'Exception',
          'Rewards More Ways To Earn',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'Rewards More Ways To Earn',
          generateErrorMessage(error),
        );
      } finally {
        context.actions.hideSpinner();
      }
    },
    [props.policyItem, context],
  );

  const getCompleteButton = useMemo(
    () => (
      <RewardsCompleteButton
        isCompleted={isCompleted}
        currentStatus={currentStatusForBtn}
        onPress={handleCompleteAction}
        policyType={props.policyItem?.policy?.policyType}
      />
    ),
    [
      isCompleted,
      currentStatusForBtn,
      handleCompleteAction,
      props.policyItem?.policy?.policyType,
    ],
  );
  const purchaseCountTextView = () => {
    return (
      <AVText
        style={styles.purchaseCountText}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
      >
        {Localized.Labels.formatString(
          Localized.Labels.rewards_more_purchase_count,
          props.policyItem?.currentPurchaseCount ?? 0,
          props.policyItem?.policy?.requiredPurchaseCount ?? 0,
        )}
      </AVText>
    );
  };

  return (
    <View
      style={[
        styles.container,
        props.index % Util.getNumberOfColumnsInGrid() === 0
          ? styles.containerLeft
          : styles.containerRight,
      ]}
      accessibilityLabel={props.policyItem.policyName}
      accessible
      aria-label={`${props.policyItem.policyName}`}
    >
      {getRewardImage}
      <View style={styles.contentView}>
        <AVText
          style={styles.nameText}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
        >
          {getPolicyDescription()}
        </AVText>
        <View style={styles.buttonsView}>
          {props.policyItem?.policy?.policyType ===
            RewardPolicyType.PURCHASE_STREAK && purchaseCountTextView()}
          <AVText
            style={styles.pointsText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
          >
            {Localized.Labels.formatString(
              Localized.Labels.points_number,
              props.policyItem.policy?.points,
            )}
          </AVText>
        </View>
        <View style={styles.buttonWrapper}>{getCompleteButton}</View>
      </View>
      {isModalVisible && (
        <RewardsModal
          title={Localized.Success.receipt_send_success}
          visible={isModalVisible}
          onRequestClose={() => setModalVisible(false)}
          testIDMsg="qa_Rewards.Popup.Message"
          testIDTitle="qa_Rewards.Popup.Title"
          testIDButton="qa_Rewards.Popup.Button"
          minimumSpendAmount={modalParams.minimumSpendAmount}
          returnWithinDays={modalParams.returnWithinDays}
          requiredPurchaseCount={modalParams.requiredPurchaseCount}
          currentPurchaseCount={modalParams.currentPurchaseCount}
          endDate={modalParams.endDate}
          policyType={modalParams.policyType}
          policyItem={props.policyItem}
          onStartShoppingPress={(answers) => {
            handleStartShopping(answers);
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: cardWidth,
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: Styles.white,
    borderRadius: Styles.Spacing.m2,
    borderColor: Styles.inputBgColor,
    borderWidth: 1,
    padding: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
    marginRight: gapBetweenColumns,
    ...Platform.select({
      web: {height: cardWidth * 1.15},
      ios: {
        height: cardWidth * 1.28,
      },
      android: {
        height: cardWidth * 1.33,
      },
    }),
    ...getLayoutDescriber().shadowStyles().shadow,
  },
  containerLeft: {
    ...Platform.select({
      web: {marginLeft: Styles.Spacing.m3},
      default: {
        marginLeft: Styles.Spacing.m3,
        marginRight: Styles.Spacing.m1,
      },
    }),
  },
  containerRight: {
    ...Platform.select({
      web: {marginRight: Styles.Spacing.m3},
      default: {
        marginLeft: Styles.Spacing.m1,
        marginRight: Styles.Spacing.m3,
      },
    }),
  },
  contentView: {
    marginTop: Styles.Spacing.m2 + Styles.Spacing.m1,
    justifyContent: 'space-between',
    flex: 1,
    alignItems: 'center',
  },
  nameText: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '700',
    textAlign: 'center',
    color: Styles.Colors.PayNew.black01,
    fontFamily: getToastDescriber().snackBarFontFamily(),
  },
  pointsText: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    textAlign: 'center',
    color: Styles.lightGray,
    fontFamily: getToastDescriber().snackBarFontFamily(),
  },
  purchaseCountText: {
    fontSize: Styles.Fonts.f0,
    fontWeight: '400',
    textAlign: 'center',
    color: Styles.black,
    fontStyle: 'italic',
    fontFamily: getToastDescriber().snackBarFontFamily(),
    marginBottom: 2,
  },
  buttonsView: {
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  buttonWrapper: {
    width: '100%',
    alignItems: 'center',
  },
});

export default withGlobalize(withIsConnected(RewardMoreWaysToEarnView));
